import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { followUpEvidenceQuestion, qaHref } from '../contextLinks';

interface QueueCounts {
  overdue: number;
  due_today: number;
  blocked: number;
  waiting: number;
}

interface QueueFreshness {
  newest_item_at: string | null;
  oldest_active_at: string | null;
}

interface QueueItem {
  id: string;
  type: string | null;
  status: string;
  priority: string;
  owner_email: string | null;
  accountable_email: string | null;
  queue_key: string | null;
  resolved_queue_key: string | null;
  description: string;
  due_at: string | null;
  due_date: string | null;
  waiting_on: string | null;
  blocked_reason: string | null;
  contact_id: string | null;
  source: string | null;
  source_ref: string | null;
  confidence: number | null;
  reviewed_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  is_overdue: boolean;
  is_due_today: boolean;
}

interface QueueResponse {
  queue_key: string;
  generated_at: string;
  freshness: QueueFreshness;
  counts: QueueCounts;
  items: QueueItem[];
}

interface EngineHealthPayload {
  is_stale: boolean;
  last_heartbeat_at: string | null;
  minutes_since_last_heartbeat: number | null;
  heartbeat_threshold_minutes: number;
  last_successful_ingest_at: string | null;
  minutes_since_last_success: number | null;
  stale_threshold_minutes: number;
  recent_failures: Array<{ at: string; error_message: string }>;
  recent_call_volume: { last_24h: number; last_7d: number };
  recent_contact_link_rate: number | null;
}

interface CatalogHealth {
  pending: number;
  thin_margin: number;
  approved_today: number;
  catalog_health_pct: number | null;
}

interface CatalogProposalsResponse {
  total: number;
  proposals: unknown[];
}

interface PlaudReviewResponse {
  total: number;
  groups: Array<{ items: unknown[] }>;
}

interface QaCandidatesResponse {
  candidates: unknown[];
}

interface BriefCandidatesResponse {
  qa_bank_candidates: unknown[];
}

interface ReviewBacklog {
  catalogPending: number | null;
  catalogThinMargin: number | null;
  plaudPending: number | null;
  qaPending: number | null;
}

interface CoreState {
  loading: boolean;
  error: string | null;
  erikQueue: QueueResponse | null;
  vinceQueue: QueueResponse | null;
  engineHealth: EngineHealthPayload | null;
  engineHealthUnavailable: boolean;
}

const ERIK_TYPES = new Set([
  'pricing_decision',
  'scope_decision',
  'technical_sales_escalation',
  'quote_revision',
  'final_pricing_authority',
]);

const TYPE_TO_ROLE: Record<string, string> = {
  intake_missing_info: 'Ron',
  customer_success_followup: 'Ron',
  quote_review_scheduling: 'Ron',
  reactivation: 'Ron',
  customer_waiting_followup: 'Ron',
  ops_handoff: 'Vince',
  production_handoff_gap: 'Vince',
  vendor_followup: 'Vince',
  schedule_confirm: 'Vince',
  parts_eta: 'Vince',
  customer_ops_update: 'Vince',
  warranty_logistics: 'Vince',
  electrical_question: 'Nate',
  shop_feasibility: 'Nate',
  production_clarification: 'Nate',
  qc_finding: 'Nate',
  technical_photo_review: 'Nate',
  pricing_decision: 'Erik',
  scope_decision: 'Erik',
  technical_sales_escalation: 'Erik',
  quote_revision: 'Erik',
  final_pricing_authority: 'Erik',
};

async function fetchJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Request failed: ${res.status}`);
  }
  return (await res.json()) as T;
}

function fulfilled<T>(result: PromiseSettledResult<T>): T | null {
  return result.status === 'fulfilled' ? result.value : null;
}

function formatTimestamp(iso: string | null): string {
  if (iso === null) return 'Unavailable';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatDate(iso: string | null): string {
  if (iso === null) return 'No due date';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatPercent(value: number | null): string {
  if (value === null) return 'Unavailable';
  return `${Math.round(value * 100)}%`;
}

function chip(classes: string, label: string): JSX.Element {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${classes}`}
    >
      {label}
    </span>
  );
}

function priorityChip(priority: string): JSX.Element {
  const classes: Record<string, string> = {
    urgent: 'bg-red-50 text-red-900 ring-red-300',
    high: 'bg-orange-50 text-orange-900 ring-orange-300',
    normal: 'bg-blue-50 text-blue-900 ring-blue-200',
    low: 'bg-slate-50 text-slate-700 ring-slate-200',
  };
  return chip(classes[priority] ?? classes.normal, priority);
}

function statusChip(status: string): JSX.Element {
  const classes: Record<string, string> = {
    open: 'bg-blue-50 text-blue-900 ring-blue-200',
    in_progress: 'bg-amber-50 text-amber-900 ring-amber-200',
    waiting: 'bg-purple-50 text-purple-900 ring-purple-200',
    blocked: 'bg-rose-50 text-rose-900 ring-rose-200',
    done: 'bg-green-50 text-green-900 ring-green-200',
    dismissed: 'bg-slate-100 text-slate-700 ring-slate-300',
  };
  return chip(classes[status] ?? classes.open, status);
}

function isPossibleMisroute(item: QueueItem): boolean {
  if (!item.type) return false;
  const expectedRole = TYPE_TO_ROLE[item.type];
  return expectedRole !== undefined && expectedRole !== 'Erik';
}

function itemDueLabel(item: QueueItem): JSX.Element | null {
  if (item.is_overdue) return chip('bg-rose-50 text-rose-900 ring-rose-300', 'overdue');
  if (item.is_due_today) return chip('bg-amber-50 text-amber-900 ring-amber-300', 'due today');
  return null;
}

function MetricCard({
  label,
  value,
  note,
  tone = 'slate',
}: {
  label: string;
  value: string | number;
  note: string;
  tone?: 'slate' | 'blue' | 'green' | 'amber' | 'rose' | 'violet';
}): JSX.Element {
  const toneClass: Record<'slate' | 'blue' | 'green' | 'amber' | 'rose' | 'violet', string> = {
    slate: 'text-slate-900',
    blue: 'text-blue-700',
    green: 'text-emerald-700',
    amber: 'text-amber-700',
    rose: 'text-rose-700',
    violet: 'text-violet-700',
  };

  return (
    <div className="min-w-0 rounded-lg bg-white p-4 shadow-sm ring-1 ring-slate-200">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-600">{label}</div>
      <div className={`mt-1 break-words text-2xl font-semibold ${toneClass[tone]}`}>{value}</div>
      <div className="mt-1 text-xs text-slate-500">{note}</div>
    </div>
  );
}

function WorkItemCard({
  item,
  misroute = false,
}: {
  item: QueueItem;
  misroute?: boolean;
}): JSX.Element {
  const due = item.due_at ?? item.due_date;
  const likelyRole = item.type ? TYPE_TO_ROLE[item.type] : null;

  return (
    <article className="border-b border-slate-100 px-5 py-4 last:border-b-0 hover:bg-slate-50">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            {item.type ? chip('bg-slate-50 text-slate-700 ring-slate-200', item.type) : null}
            {itemDueLabel(item)}
            {misroute && likelyRole ? (
              chip('bg-cyan-50 text-cyan-900 ring-cyan-200', `likely ${likelyRole}`)
            ) : null}
          </div>
          <p className="text-sm font-medium leading-6 text-slate-950">{item.description}</p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {statusChip(item.status)}
            {priorityChip(item.priority)}
            {item.waiting_on ? (
              <span className="text-xs text-slate-600">Waiting: {item.waiting_on}</span>
            ) : null}
            {item.blocked_reason ? (
              <span className="text-xs text-rose-700">Blocked: {item.blocked_reason}</span>
            ) : null}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-500">
            <span>Due: {formatDate(due)}</span>
            <span>Updated: {formatTimestamp(item.updated_at)}</span>
            {item.source ? <span>Source: {item.source}</span> : null}
            {item.confidence !== null ? <span>Confidence: {formatPercent(item.confidence)}</span> : null}
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2 sm:flex-col sm:text-right">
          {item.contact_id ? (
            <Link
              to={`/contacts/${item.contact_id}`}
              className="rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-50"
            >
              Customer evidence
            </Link>
          ) : null}
          <Link
            to={qaHref(followUpEvidenceQuestion({
              contactId: item.contact_id,
              description: item.description,
              type: item.type,
              source: item.source,
              due,
            }))}
            className="rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100"
          >
            Ask evidence
          </Link>
        </div>
      </div>
    </article>
  );
}

function Panel({
  title,
  subtitle,
  children,
  action,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  action?: ReactNode;
}): JSX.Element {
  return (
    <section className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-slate-200">
      <header className="flex items-start justify-between gap-4 border-b border-slate-200 bg-slate-50 px-5 py-4">
        <div>
          <h2 className="text-base font-semibold text-slate-950">{title}</h2>
          {subtitle ? <p className="mt-1 text-xs text-slate-600">{subtitle}</p> : null}
        </div>
        {action}
      </header>
      {children}
    </section>
  );
}

function ReviewBacklogPanel({
  review,
  loading,
}: {
  review: ReviewBacklog;
  loading: boolean;
}): JSX.Element {
  const rows = [
    {
      label: 'Product proposals',
      note: 'Odoo product boundary',
      count: review.catalogPending,
      subCount: review.catalogThinMargin === null ? null : `${review.catalogThinMargin} thin margin`,
      to: '/product-proposals',
    },
    {
      label: 'Plaud candidates',
      note: 'Candidate review only',
      count: review.plaudPending,
      subCount: null,
      to: '/plaud/review',
    },
    {
      label: 'Q&A candidates',
      note: 'Knowledge arbiter queue',
      count: review.qaPending,
      subCount: null,
      to: '/qa-bank/review',
    },
  ];

  return (
    <Panel title="High-risk review links" subtitle="Counts are helpful; links are required.">
      <div className="divide-y divide-slate-100">
        {rows.map((row) => (
          <Link
            key={row.to}
            to={row.to}
            className="flex items-center justify-between gap-4 px-5 py-3 hover:bg-slate-50"
          >
            <span>
              <span className="block text-sm font-medium text-slate-950">{row.label}</span>
              <span className="block text-xs text-slate-500">
                {row.note}
                {row.subCount ? ` · ${row.subCount}` : ''}
              </span>
            </span>
            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700">
              {loading ? '...' : row.count ?? 'open'}
            </span>
          </Link>
        ))}
      </div>
    </Panel>
  );
}

function EngineHealthPanel({
  payload,
  unavailable,
}: {
  payload: EngineHealthPayload | null;
  unavailable: boolean;
}): JSX.Element {
  const status = unavailable
    ? { label: 'Unavailable', cls: 'bg-slate-300', text: 'text-slate-700' }
    : payload?.is_stale
      ? { label: 'Stale', cls: 'bg-rose-500', text: 'text-rose-700' }
      : { label: 'Healthy', cls: 'bg-emerald-500', text: 'text-emerald-700' };

  return (
    <Panel title="System trust" subtitle="Act only if the evidence layer is fresh enough to trust.">
      <div className="space-y-3 px-5 py-4 text-sm">
        <div className="flex items-center justify-between gap-4">
          <span className="text-slate-600">Status</span>
          <span className={`inline-flex items-center gap-2 font-semibold ${status.text}`}>
            <span className={`h-2.5 w-2.5 rounded-full ${status.cls}`} />
            {status.label}
          </span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="text-slate-600">Last heartbeat</span>
          <span className="font-medium text-slate-950">
            {payload ? formatTimestamp(payload.last_heartbeat_at) : 'Unavailable'}
          </span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="text-slate-600">Last ingest</span>
          <span className="font-medium text-slate-950">
            {payload ? formatTimestamp(payload.last_successful_ingest_at) : 'Unavailable'}
          </span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="text-slate-600">Calls, 24h / 7d</span>
          <span className="font-medium text-slate-950">
            {payload
              ? `${payload.recent_call_volume.last_24h} / ${payload.recent_call_volume.last_7d}`
              : 'Unavailable'}
          </span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="text-slate-600">Contact link rate</span>
          <span className="font-medium text-slate-950">
            {payload ? formatPercent(payload.recent_contact_link_rate) : 'Unavailable'}
          </span>
        </div>
        {payload && payload.recent_failures.length > 0 ? (
          <div className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900 ring-1 ring-amber-200">
            {payload.recent_failures.length} recent ingest failure
            {payload.recent_failures.length === 1 ? '' : 's'}.
          </div>
        ) : null}
      </div>
    </Panel>
  );
}

export function ErikExceptionHome(): JSX.Element {
  const [core, setCore] = useState<CoreState>({
    loading: true,
    error: null,
    erikQueue: null,
    vinceQueue: null,
    engineHealth: null,
    engineHealthUnavailable: false,
  });
  const [review, setReview] = useState<ReviewBacklog>({
    catalogPending: null,
    catalogThinMargin: null,
    plaudPending: null,
    qaPending: null,
  });
  const [reviewLoading, setReviewLoading] = useState(true);

  const loadCore = useCallback(async () => {
    setCore((prev) => ({ ...prev, loading: true, error: null }));
    const [erikResult, healthResult, vinceResult] = await Promise.allSettled([
      fetchJson<QueueResponse>('/api/queues/erik-escalations'),
      fetchJson<EngineHealthPayload>('/api/admin/engine-health'),
      fetchJson<QueueResponse>('/api/queues/vince-handoff'),
    ]);

    const erikQueue = fulfilled(erikResult);
    if (!erikQueue) {
      const message = erikResult.status === 'rejected' ? erikResult.reason.message : 'Unknown error';
      setCore({
        loading: false,
        error: message,
        erikQueue: null,
        vinceQueue: fulfilled(vinceResult),
        engineHealth: fulfilled(healthResult),
        engineHealthUnavailable: healthResult.status === 'rejected',
      });
      return;
    }

    setCore({
      loading: false,
      error: null,
      erikQueue,
      vinceQueue: fulfilled(vinceResult),
      engineHealth: fulfilled(healthResult),
      engineHealthUnavailable: healthResult.status === 'rejected',
    });
  }, []);

  const loadReviewBacklog = useCallback(async () => {
    setReviewLoading(true);
    const [catalogHealthResult, catalogResult, plaudResult, qaResult, briefResult] =
      await Promise.allSettled([
        fetchJson<CatalogHealth>('/api/catalog/health'),
        fetchJson<CatalogProposalsResponse>('/api/catalog/proposals?status=pending&limit=300'),
        fetchJson<PlaudReviewResponse>('/api/plaud/review-queue?status=pending&limit=300'),
        fetchJson<QaCandidatesResponse>('/api/qa-extraction/candidates?status=pending&limit=200'),
        fetchJson<BriefCandidatesResponse>('/api/brief-candidates'),
      ]);

    const catalogHealth = fulfilled(catalogHealthResult);
    const catalog = fulfilled(catalogResult);
    const plaud = fulfilled(plaudResult);
    const qa = fulfilled(qaResult);
    const brief = fulfilled(briefResult);

    setReview({
      catalogPending: catalogHealth?.pending ?? catalog?.total ?? catalog?.proposals.length ?? null,
      catalogThinMargin: catalogHealth?.thin_margin ?? null,
      plaudPending: plaud?.total ?? plaud?.groups.reduce((sum, group) => sum + group.items.length, 0) ?? null,
      qaPending: qa || brief ? (qa?.candidates.length ?? 0) + (brief?.qa_bank_candidates.length ?? 0) : null,
    });
    setReviewLoading(false);
  }, []);

  useEffect(() => {
    void loadCore();
    void loadReviewBacklog();
  }, [loadCore, loadReviewBacklog]);

  const misroutes = useMemo(
    () => core.erikQueue?.items.filter(isPossibleMisroute) ?? [],
    [core.erikQueue],
  );
  const trueDecisions = useMemo(
    () => core.erikQueue?.items.filter((item) => !isPossibleMisroute(item)) ?? [],
    [core.erikQueue],
  );

  const erikCount = core.erikQueue?.items.length ?? 0;
  const vinceCount = core.vinceQueue?.items.length ?? null;
  const healthLabel = core.engineHealthUnavailable
    ? 'Unavailable'
    : core.engineHealth?.is_stale
      ? 'Stale'
      : 'OK';

  return (
    <>
      <header className="mb-6">
        <div className="text-xs font-semibold uppercase tracking-wide text-cyan-700">
          Role home pilot
        </div>
        <div className="mt-1 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-950">
              Exception review
            </h1>
            <p className="mt-1 max-w-3xl text-sm text-slate-600">
              True owner decisions, routing mistakes, and system-trust risks. This page links to
              existing review surfaces; it does not approve, promote, reassign, or write to Odoo.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link
              to="/qa-bank/review"
              className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Review Q&A
            </Link>
            <Link
              to="/product-proposals"
              className="rounded-md bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800"
            >
              Product proposals
            </Link>
          </div>
        </div>
      </header>

      {core.error ? (
        <div className="mb-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-800 ring-1 ring-red-200">
          Failed to load Erik exceptions: {core.error}
        </div>
      ) : null}

      {core.loading ? (
        <div className="rounded-lg bg-white p-8 text-center text-slate-500 shadow-sm ring-1 ring-slate-200">
          Loading Erik exception home...
        </div>
      ) : (
        <>
          <section className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <MetricCard
              label="Needs decision"
              value={trueDecisions.length}
              note={`${erikCount} active Erik queue item${erikCount === 1 ? '' : 's'}`}
              tone={trueDecisions.length > 0 ? 'rose' : 'green'}
            />
            <MetricCard
              label="Overdue"
              value={core.erikQueue?.counts.overdue ?? 0}
              note="oldest first in queue"
              tone={(core.erikQueue?.counts.overdue ?? 0) > 0 ? 'amber' : 'slate'}
            />
            <MetricCard
              label="Blocked"
              value={core.erikQueue?.counts.blocked ?? 0}
              note="needs evidence or owner call"
              tone={(core.erikQueue?.counts.blocked ?? 0) > 0 ? 'violet' : 'slate'}
            />
            <MetricCard
              label="Possible misroutes"
              value={misroutes.length}
              note="looks routable away"
              tone={misroutes.length > 0 ? 'blue' : 'slate'}
            />
            <MetricCard
              label="System trust"
              value={healthLabel}
              note={
                core.engineHealth
                  ? `heartbeat ${formatTimestamp(core.engineHealth.last_heartbeat_at)}`
                  : 'health unavailable'
              }
              tone={healthLabel === 'OK' ? 'green' : healthLabel === 'Stale' ? 'rose' : 'amber'}
            />
            <MetricCard
              label="Vince after PTO"
              value={vinceCount ?? 'open'}
              note="validate when Vince returns"
              tone="slate"
            />
          </section>

          <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1.4fr)_minmax(320px,0.8fr)]">
            <div className="min-w-0 space-y-5">
              <Panel
                title="True Erik decisions"
                subtitle="Escalations whose type matches owner authority."
                action={
                  core.erikQueue?.generated_at ? (
                    <span className="text-xs text-slate-500">
                      Generated {formatTimestamp(core.erikQueue.generated_at)}
                    </span>
                  ) : null
                }
              >
                {trueDecisions.length === 0 ? (
                  <div className="px-5 py-8 text-center text-sm text-slate-500">
                    No active Erik decision items. Check system trust and review backlog before
                    moving on.
                  </div>
                ) : (
                  <div>
                    {trueDecisions.map((item) => (
                      <WorkItemCard key={item.id} item={item} />
                    ))}
                  </div>
                )}
              </Panel>

              <Panel
                title="Possible misroutes"
                subtitle="Items in Erik's queue that look like another role's work."
                action={
                  <span className="rounded-full bg-cyan-50 px-2.5 py-1 text-xs font-semibold text-cyan-900 ring-1 ring-cyan-200">
                    Review only
                  </span>
                }
              >
                {misroutes.length === 0 ? (
                  <div className="px-5 py-8 text-center text-sm text-slate-500">
                    No obvious misroutes in Erik's active queue.
                  </div>
                ) : (
                  <div>
                    {misroutes.map((item) => (
                      <WorkItemCard key={item.id} item={item} misroute />
                    ))}
                  </div>
                )}
              </Panel>
            </div>

            <div className="min-w-0 space-y-5">
              <EngineHealthPanel
                payload={core.engineHealth}
                unavailable={core.engineHealthUnavailable}
              />

              <ReviewBacklogPanel review={review} loading={reviewLoading} />

              <Panel
                title="Vince after PTO"
                subtitle="Do not proxy a full Vince workflow while he is out."
                action={
                  <Link
                    to="/queues/vince"
                    className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-50"
                  >
                    Open Vince queue
                  </Link>
                }
              >
                <div className="space-y-3 px-5 py-4 text-sm">
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-slate-600">Handoff items</span>
                    <span className="font-medium text-slate-950">{vinceCount ?? 'Unavailable'}</span>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-slate-600">Blocked</span>
                    <span className="font-medium text-slate-950">
                      {core.vinceQueue?.counts.blocked ?? 'Unavailable'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-slate-600">Oldest active</span>
                    <span className="font-medium text-slate-950">
                      {core.vinceQueue
                        ? formatTimestamp(core.vinceQueue.freshness.oldest_active_at)
                        : 'Unavailable'}
                    </span>
                  </div>
                  <p className="rounded-md bg-slate-50 px-3 py-2 text-xs leading-5 text-slate-600 ring-1 ring-slate-200">
                    Use this as a parking lot for validation questions. Build Vince Home after
                    Vince can validate the real workflow.
                  </p>
                </div>
              </Panel>
            </div>
          </div>
        </>
      )}
    </>
  );
}
